import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Check, X, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";

import clubbieLogo from "../../assets/images/clubbie_logo_full.svg";
import { config } from "../../utils/config";
import TeamBadge from "../../components/TeamBadge/TeamBadge";
import TournamentBadge from "../../components/TournamentBadge/TournamentBadge";

/**
 * Public Invitation Response Page
 * Allows users to accept or reject tournament invitations via email link
 * No authentication required
 */
const InvitationResponse = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get query params from URL
  const tournamentId = searchParams.get("tournamentId");
  const categoryId = searchParams.get("categoryId") || searchParams.get("divisionId");
  const teamId = searchParams.get("teamId");

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [invitationDetails, setInvitationDetails] = useState(null);
  const [responseStatus, setResponseStatus] = useState(null); // 'accepted' | 'rejected' | null

  // Handle navigation to team dashboard
  const handleContinueToDashboard = () => {
    const params = new URLSearchParams({
      token: token, // Pass token for player management authorization
      teamName: invitationDetails?.teamName || "",
      tournamentName: invitationDetails?.tournamentName || "",
      tournamentId: tournamentId || "",
      categoryId: categoryId || "",
    });
    navigate(`/team/${teamId}/dashboard?${params.toString()}`);
  };

  // Fetch invitation details on mount
  useEffect(() => {
    const fetchInvitationDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(
          `${config.API_BASE_URL}/${config.API_PATH}/tournaments/invitation/${token}`,
          {
            params: { tournamentId, categoryId, teamId }
          }
        );

        setInvitationDetails(response.data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
          "Unable to load invitation details. The link may be invalid or expired."
        );
      } finally {
        setLoading(false);
      }
    };

    if (token && tournamentId && categoryId && teamId) {
      fetchInvitationDetails();
    } else {
      setError("Invalid invitation link. Missing required parameters.");
      setLoading(false);
    }
  }, [token, tournamentId, categoryId, teamId]);

  // Handle accept/reject
  const handleResponse = async (response) => {
    try {
      setSubmitting(true);
      setError(null);

      const result = await axios.post(
        `${config.API_BASE_URL}/${config.API_PATH}/tournaments/invitation/${token}`,
        {
          tournamentId,
          categoryId,
          teamId,
          response, // 'accept' or 'reject'
        }
      );

      setResponseStatus(response === "accept" ? "accepted" : "rejected");
      setInvitationDetails((prev) => ({
        ...prev,
        status: result.data.status,
        canRespond: false,
      }));
    } catch (err) {
      setError(
        err.response?.data?.message ||
        "Failed to submit your response. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-lg w-full text-center">
          <div className="flex items-center justify-center mb-5">
            <img className="h-auto w-[160px]" src={clubbieLogo} alt="clubbie logo" />
          </div>
          <Loader2 className="w-10 h-10 text-[#00E7FF] animate-spin mx-auto mb-3" />
          <p className="text-app-text-secondary text-sm">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !invitationDetails) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-lg w-full text-center">
          <div className="flex items-center justify-center mb-5">
            <img className="h-auto w-[160px]" src={clubbieLogo} alt="clubbie logo" />
          </div>
          <AlertCircle className="w-12 h-12 text-[#FF7B7B] mx-auto mb-4" />
          <h1 className="text-[18px] font-semibold text-app-text mb-2">
            Unable to Load Invitation
          </h1>
          <p className="text-app-text-secondary text-sm mb-6">{error}</p>
          <div className="flex justify-end">
            <a
              href="/"
              className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all"
            >
              Go to Homepage
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Already responded state
  const normalizedStatus = invitationDetails?.status?.toLowerCase();
  const isAccepted = normalizedStatus === "accepted" || normalizedStatus === "accept";
  if (invitationDetails && !invitationDetails.canRespond && !responseStatus) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-lg w-full text-center">
          <div className="flex items-center justify-center mb-5">
            <img className="h-auto w-[160px]" src={clubbieLogo} alt="clubbie logo" />
          </div>
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${isAccepted ? "bg-[#4AED88]/10 border border-[#4AED88]/30" : "bg-[#FF7B7B]/10 border border-[#FF7B7B]/30"}`}>
            {isAccepted ? (
              <Check className="w-7 h-7 text-[#4AED88]" />
            ) : (
              <X className="w-7 h-7 text-[#FF7B7B]" />
            )}
          </div>
          <h1 className="text-[18px] font-semibold text-app-text mb-2">
            Invitation already {isAccepted ? "accepted" : "declined"}
          </h1>
          <p className="text-app-text-secondary text-sm mb-6">
            This invitation for <span className="text-app-text">{invitationDetails.teamName}</span> to join{" "}
            <span className="text-app-text">{invitationDetails.tournamentName}</span> has already been{" "}
            {isAccepted ? "accepted" : "declined"}.
          </p>
          {isAccepted && (
            <div className="flex justify-center">
              <button
                onClick={handleContinueToDashboard}
                className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all"
              >
                Go to dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Success response state
  if (responseStatus) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-lg w-full text-center">
          <div className="flex items-center justify-center mb-5">
            <img className="h-auto w-[160px]" src={clubbieLogo} alt="clubbie logo" />
          </div>
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${responseStatus === "accepted" ? "bg-[#4AED88]/10 border border-[#4AED88]/30" : "bg-[#FF7B7B]/10 border border-[#FF7B7B]/30"}`}>
            {responseStatus === "accepted" ? (
              <Check className="w-7 h-7 text-[#4AED88]" />
            ) : (
              <X className="w-7 h-7 text-[#FF7B7B]" />
            )}
          </div>
          <h1 className="text-[18px] font-semibold text-app-text mb-2">
            {responseStatus === "accepted" ? "Invitation Accepted!" : "Invitation Declined"}
          </h1>
          <p className="text-app-text-secondary text-sm mb-6">
            {responseStatus === "accepted" ? (
              <>
                <span className="text-app-text">{invitationDetails?.teamName}</span> has been added to{" "}
                <span className="text-app-text">{invitationDetails?.tournamentName}</span>.{" "}
                You can now participate in the tournament.
              </>
            ) : (
              <>
                You have declined the invitation to join{" "}
                <span className="text-app-text">{invitationDetails?.tournamentName}</span>.
              </>
            )}
          </p>
          {responseStatus === "accepted" && (
            <div className="flex justify-center">
              <button
                onClick={handleContinueToDashboard}
                className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all"
              >
                Continue to dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main invitation view
  return (
    <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
      <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-lg w-full">
        {/* Header */}
        <div className="text-center items-center justify-center mb-2">
          <div className="flex items-center justify-center mb-5">
            <img
              className="h-auto w-[160px]"
              src={clubbieLogo}
              alt="clubbie logo"
            />
          </div>
          <p className="text-app-text text-[18px] font-medium">
            {`Your team has been invited to paritcipate in ${invitationDetails?.tournamentName}`}
          </p>
        </div>

        {/* Invitation Details Card */}
        <div className="flex items-start justify-center py-6">
          {/* Tournament Info */}
          <div className="flex flex-col w-[50%] min-w-0 items-center justify-start text-center gap-4 px-2">
            <TournamentBadge containerSize="w-[70px] h-[70px]" size="w-6 h-6" src={invitationDetails?.tournamentLogo} alt={invitationDetails?.tournamentName} />
            <div className="w-full">
              <h3 className="text-lg font-semibold text-app-text break-words">
                {invitationDetails?.tournamentName}
                <span className="text-app-text-secondary">
                    {` ${invitationDetails?.categoryName || invitationDetails?.divisionName}`}
                </span>
              </h3>
            </div>
          </div>

          {/* Team Info */}
          <div className="flex flex-col w-[50%] min-w-0 text-center items-center justify-start gap-4 px-2">
            <TeamBadge containerSize="w-[70px] h-[70px]" size="w-5 h-5" src={invitationDetails?.teamCrest} alt={invitationDetails?.teamName} />
            <div className="w-full">
              <h3 className="text-lg font-semibold text-[#FEFCEB] break-words">
                {invitationDetails?.teamName}
              </h3>
            </div>
          </div>
        </div>

        {/* invitation message */}
        <div className="mb-6 text-center">
          <p className="text-app-text text-[15px]">
            {`${invitationDetails?.organizerName}`} <span className="text-app-text-secondary">has invited you to join</span> {`${invitationDetails?.tournamentName} `} 
            <span className="text-app-text-secondary">with your team</span> {`${invitationDetails?.teamName}.`}
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-[#FF7B7B]/10 border border-[#FF7B7B]/30 rounded-lg p-4 mb-6">
            <p className="text-[#FF7B7B] text-sm">{error}</p>
          </div>
        )}

        {/* Expired notice */}
        {invitationDetails?.isExpired && (
          <div className="bg-[#FF7B7B]/10 border border-[#FF7B7B]/30 rounded-lg p-4 mb-6">
            <p className="text-[#FF7B7B] text-sm">
              This invitation has expired. Please contact the tournament organizer.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        {!invitationDetails?.isExpired && (
          <div className="flex gap-3">
            <button
              onClick={() => handleResponse("reject")}
              disabled={submitting}
              className="flex-1 px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Decline
            </button>
            <button
              onClick={() => handleResponse("accept")}
              disabled={submitting}
              className="flex-1 bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : null}
              Accept invitation
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitationResponse;
